import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, downloadFile, blobErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, History, Sparkles, FileText, Sun, Trash2, Archive } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AddToMyDayDialog } from "@/components/workspace/AddToMyDayDialog";
import { DeleteProjectDialog } from "@/components/workspace/DeleteProjectDialog";
import { PhaseTimeline } from "@/components/workspace/PhaseTimeline";
import { ProjectAssistant } from "@/components/workspace/ProjectAssistant";
import { ReportDialog, PrototypeReportButton } from "@/components/workspace/Reports";
import { PhaseHeader } from "@/components/workspace/PhaseHeader";
import { PhaseActions } from "@/components/workspace/PhaseActions";
import { PhaseEvidence } from "@/components/workspace/PhaseEvidence";
import { PhaseNotes } from "@/components/workspace/PhaseNotes";
import { ReadinessChecklist } from "@/components/workspace/Readiness";
import { EconomicFilePanel, ErpHandoverPanel } from "@/components/workspace/EconomicFile";
import { ProjectMembers } from "@/components/projects/ProjectMembers";
import { statusClass, statusLabel, fmtDate } from "@/lib/workflow";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [ws, setWs] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState(params.get("phase"));
  const [highlight, setHighlight] = useState(params.get("action"));
  const [attachTarget, setAttachTarget] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showMyDay, setShowMyDay] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [dlArchive, setDlArchive] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    const [w, u, a] = await Promise.all([api.get(`/projects/${id}/workspace`), api.get("/users"), api.get(`/projects/${id}/activities`)]);
    setWs((prev) => {
      if (prev) {
        for (const p of w.data.phases) {
          const old = prev.phases.find((x) => x.phase_id === p.phase_id);
          const last = p.history?.[p.history.length - 1];
          if (old && old.status !== p.status && last?.auto) {
            if (p.status === "completed") toast.success(`${p.name}: phase completed automatically — all requirements are satisfied.`);
            else if (old.status === "completed") toast.info(`${p.name}: phase reopened automatically — ${last.reason?.replace("Reopened automatically: ", "") || "requirements no longer satisfied"}`);
          }
        }
      }
      return w.data;
    });
    setUsers(u.data);
    setActivities(a.data);
    setSelected((s) => (s && w.data.phases.some((p) => p.phase_id === s) ? s : w.data.focus_phase_id));
  }, [id]);

  useEffect(() => { load().catch(() => toast.error("Project not found")); }, [load]);

  if (!ws) return <div className="p-10 text-slate-500">Loading project workspace…</div>;

  const { project, phases, readiness } = ws;
  const phase = phases.find((p) => p.phase_id === selected) || phases[0];
  const canArchive = ["admin", "project_coordinator"].includes(user?.role) || project.project_manager_id === user?.user_id;
  const downloadArchive = async () => {
    setDlArchive(true);
    try {
      const name = await downloadFile(`/projects/${project.project_id}/archive/download`, `${project.project_number}.zip`);
      toast.success(`Project Archive generated — ${name}`);
      load();
    } catch (e) { toast.error(await blobErrorDetail(e)); } finally { setDlArchive(false); }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 space-y-6" data-testid="project-workspace">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Link to="/projects" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"><ChevronLeft className="h-4 w-4 mr-1" /> Project Registry</Link>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowMyDay(true)} data-testid="add-to-my-day-btn"><Sun className="h-4 w-4 mr-2 text-amber-500" />Add to My Day</Button>
          <Button variant="outline" size="sm" onClick={() => setShowReport(true)} data-testid="project-report-btn"><FileText className="h-4 w-4 mr-2" />Export / Report</Button>
          {canArchive && <Button variant="outline" size="sm" onClick={downloadArchive} disabled={dlArchive} data-testid="project-archive-download-btn"><Archive className="h-4 w-4 mr-2" />{dlArchive ? "Preparing…" : "Download Archive"}</Button>}
          <Button variant={showAssistant ? "default" : "outline"} size="sm" className={showAssistant ? "bg-slate-900" : ""} onClick={() => setShowAssistant((v) => !v)} data-testid="toggle-assistant-btn"><Sparkles className="h-4 w-4 mr-2" />Ask AI</Button>
          <Button variant="ghost" size="sm" onClick={() => setShowActivity((v) => !v)} data-testid="toggle-activity-log-btn"><History className="h-4 w-4 mr-2" />Audit log ({activities.length})</Button>
          {user?.role === "admin" && <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setShowDelete(true)} data-testid="delete-project-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>}
        </div>
      </div>

      <ReportDialog project={project} open={showReport} onClose={() => setShowReport(false)} />
      {showAssistant && <ProjectAssistant project={project} onClose={() => setShowAssistant(false)} />}
      {showMyDay && <AddToMyDayDialog project={project} onClose={() => setShowMyDay(false)} />}
      {showDelete && <DeleteProjectDialog project={project} onClose={() => setShowDelete(false)} />}

      {/* Header */}
      <Card className="p-6 border-slate-200">
        <div className="grid gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs bg-slate-900 text-white rounded px-2 py-1" data-testid="project-code">{project.project_number}</span>
              <Badge variant="outline" className="capitalize" data-testid="project-status-badge">{project.status?.replace(/_/g, " ")}</Badge>
              {project.status === "archived" && ["admin", "project_coordinator", "project_manager"].includes(user?.role) && (
                <Button size="sm" variant="outline" data-testid="project-restore-btn"
                  onClick={async () => { try { await api.post(`/projects/${project.project_id}/restore`); toast.success("Project restored"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); } }}>
                  Restore
                </Button>
              )}
              {project.blocked && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Blocked</Badge>}
              <Badge variant="outline" className={statusClass(project.erp_ready ? "completed" : "in_progress")}>Readiness {project.readiness_pct ?? 0}%</Badge>
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="project-detail-header-title">{project.customer_name} <span className="text-slate-400 font-normal">/</span> {project.brand}</h1>
            <p className="mt-1 text-sm text-slate-700" data-testid="project-description">{project.description}</p>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
              <span>PM <strong className="text-slate-800">{project.project_manager_name}</strong></span>
              <span>Type <strong className="text-slate-800 capitalize">{project.project_type}</strong></span>
              <span>Target <span className="font-mono text-slate-800">{project.target_date}</span></span>
              <span>Current phase <strong className="text-slate-800">{project.current_phase_name}</strong></span>
            </div>
          </div>
        </div>
        <Separator className="my-6" />
        <PhaseTimeline phases={phases} selectedId={phase.phase_id} onSelect={(pid) => { setSelected(pid); setAttachTarget(null); }} />
      </Card>

      <ProjectMembers projectId={id} />

      {showActivity && (
        <Card className="p-5 border-slate-200" data-testid="activity-log-card">
          <p className="overline">Audit log</p>
          <div className="mt-3 space-y-2 max-h-80 overflow-auto">
            {activities.map((a) => (
              <div key={a.activity_id} className="text-sm text-slate-800"><strong>{a.user_name}</strong> <span className="text-slate-500">{a.action.replace(/_/g, " ")}</span>{a.details && ` — ${a.details}`}<span className="ml-2 text-[11px] font-mono text-slate-400">{fmtDate(a.created_at)}</span></div>
            ))}
          </div>
        </Card>
      )}

      {/* Phase workspace */}
      <Card className="p-6 border-slate-200 space-y-8" key={phase.phase_id} data-testid={`phase-panel-${phase.key}`}>
        <PhaseHeader project={project} phase={phase} users={users} onChanged={load} />
        {phase.key === "erp_handover" && <ReadinessChecklist readiness={readiness} onSelectPhase={setSelected} />}
        {phase.key !== "erp_handover" && <PhaseActions project={project} phase={phase} users={users} onChanged={load} highlightId={highlight} onHighlighted={() => setHighlight(null)} onAttach={(a) => { setAttachTarget(a); document.getElementById("evidence-anchor")?.scrollIntoView({ behavior: "smooth", block: "center" }); }} />}
        {phase.key === "costing_quotation" && <EconomicFilePanel project={project} onChanged={load} />}
        {phase.key === "prototyping" && <div className="flex justify-end"><PrototypeReportButton project={project} /></div>}
        <div id="evidence-anchor" />
        {phase.key !== "erp_handover" && <PhaseEvidence project={project} phase={phase} allDocuments={ws.documents} onChanged={load} attachTarget={attachTarget} setAttachTarget={setAttachTarget} />}
        {phase.key === "erp_handover" && <ErpHandoverPanel project={project} enabled={readiness.ready} onChanged={load} />}
        <PhaseNotes project={project} phase={phase} users={users} onChanged={load} />
      </Card>

      <p className="text-[11px] text-slate-400">Phase status legend: {["not_started", "in_progress", "blocked", "completed", "not_applicable"].map((s) => statusLabel(s)).join(" · ")}</p>
    </div>
  );
}
